import React from 'react';
import cx from 'classnames';
import { isString } from 'lodash';
import isFunction from 'lodash/isFunction';

import { ArrowDownIcon } from '@motorway/mw-highway-code';

import ConditionalWrapper from 'Components/ConditionalWrapper/ConditionalWrapper';
import {
	DEFAULT_ARIA_INDEX_OFFSET,
	DEFAULT_COLUMN_SORTABLE,
	DEFAULT_COLUMN_TEXT_ALIGNMENT,
	SORT_ARIA_MAP,
} from 'Storybook/core/Datagrid/Datagrid.consts';
import type { DatagridHeaderCellProps } from 'Storybook/core/Datagrid/Datagrid.types';
import { applyCypressData } from 'Utilities/helpers';
import useKeyboardNavigation from 'Utilities/hooks/useKeyboardNavigation';

import { getNextSortDirection } from './DatagridHeaderCell.helpers';

import styles from './DatagridHeaderCell.module.scss';

const SortArrow = ({ sortDirection }: Pick<DatagridHeaderCellProps, 'sortDirection'>) => (
	<span className={styles.chevron} data-state={sortDirection}>
		<ArrowDownIcon aria-hidden="true" size="xsmall" />
	</span>
);

const DatagridHeaderCell: React.FC<DatagridHeaderCellProps> = ({
	cypressId = 'datagrid-header-cell',
	id,
	index,
	onSortHandler,
	sortable = DEFAULT_COLUMN_SORTABLE,
	sortDirection,
	text,
	textAlign = DEFAULT_COLUMN_TEXT_ALIGNMENT,
}) => {
	const isSortable = sortable && isFunction(onSortHandler);
	const isColumnSorted = sortable && sortDirection && onSortHandler;

	const onClick = () => {
		const nextSortDirection = getNextSortDirection(sortDirection);

		if (isSortable) {
			onSortHandler(id, nextSortDirection);
		}
	};

	const { onKeyDown, onKeyUp } = useKeyboardNavigation(onClick);
	const isEmpty = text === undefined || (isString(text) && text.length === 0);

	const interactiveProps = {
		onClick,
		onKeyDown,
		onKeyUp,
		scope: 'col',
	};

	return (
		<div
			{...(isSortable ? interactiveProps : {})}
			{...applyCypressData(cypressId)}
			aria-colindex={index + DEFAULT_ARIA_INDEX_OFFSET}
			aria-sort={sortDirection ? SORT_ARIA_MAP[sortDirection] : 'none'}
			className={cx(styles.datagridHeaderCell, styles[textAlign], {
				[styles.sortable]: isSortable,
				[styles.sorted]: isColumnSorted,
			})}
			id={id}
			role={isEmpty ? 'none' : 'columnheader'}
			tabIndex={isEmpty ? -1 : 0}
		>
			<ConditionalWrapper
				condition={isSortable}
				wrapper={(children) => (
					<button className={styles.invisibleButton} tabIndex={-1}>
						{children}
					</button>
				)}
			>
				<>
					<span className={styles.text}>{text}</span>
					{isSortable && <SortArrow sortDirection={sortDirection} />}
				</>
			</ConditionalWrapper>
		</div>
	);
};

export default DatagridHeaderCell;
